import { Component, computed, inject, OnDestroy, OnInit, signal, ViewEncapsulation } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialog, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { TranslatePipe } from '@ngx-translate/core';
import { QRCodeComponent } from 'angularx-qrcode';
import { apiErrorMessage } from 'shared-lib';
import { EasypassCodeDto, EasypassDto, ParkingTypeService } from '../../../../api/gen';
import { environment } from '../../../../environments/environment';

export interface ExitTicketPrintDialogData {
  easypass: EasypassDto;
  parkingOperatorId: string | undefined;
  /** IANA zone of the site, so "valid until" prints the site's day. Null = Austria. */
  timeZoneId?: string | null;
}

/** Opens the print sheet full-screen. Shared by the issue dialog and the invitation list. */
export function openExitTicketPrint(
  dialog: MatDialog, easypass: EasypassDto, parkingOperatorId: string | undefined, timeZoneId?: string | null
): void {
  dialog.open(ExitTicketPrintDialogComponent, {
    width: '100vw',
    maxWidth: '100vw',
    height: '100vh',
    autoFocus: false,
    // The print CSS keeps only this pane; the issue dialog stays open underneath and must not print.
    panelClass: 'ept-print-pane',
    data: { easypass, parkingOperatorId, timeZoneId } satisfies ExitTicketPrintDialogData
  });
}

/** Must match the 2 × 4 grid of `.ept-page` in the stylesheet. */
const TICKETS_PER_PAGE = 8;

/**
 * The printable exit tickets of one Easypass — Manuel's draft, eight per A4 page. Only unused codes are
 * printed: a used ticket opens nothing, so printing it again would only hand out a dud.
 *
 * Printing from inside a Material dialog: the dialog lives in the CDK overlay, which is fixed and
 * clipped to the viewport, so a plain window.print() would print one page of it over the app. While
 * printing, `body.ept-printing` hides the app and lets the overlay flow as an ordinary document (see
 * the stylesheet, which is unencapsulated for that reason and prefixed `ept-`).
 */
@Component({
  selector: 'app-exit-ticket-print-dialog',
  standalone: true,
  imports: [MatDialogModule, MatIconModule, MatProgressSpinnerModule, QRCodeComponent, TranslatePipe],
  templateUrl: './exit-ticket-print-dialog.component.html',
  styleUrl: './exit-ticket-print-dialog.component.scss',
  encapsulation: ViewEncapsulation.None
})
export class ExitTicketPrintDialogComponent implements OnInit, OnDestroy {
  private dialogRef = inject(MatDialogRef<ExitTicketPrintDialogComponent>);
  private parkingTypeService = inject(ParkingTypeService);
  data = inject<ExitTicketPrintDialogData>(MAT_DIALOG_DATA);

  isLoading = signal(true);
  error = signal<string | null>(null);
  codes = signal<EasypassCodeDto[]>([]);

  unusedCodes = computed(() => this.codes().filter(c => !c.redeemedAt));
  usedCount = computed(() => this.codes().length - this.unusedCodes().length);

  /** Eight tickets per A4 page: 2 columns × 4 rows. */
  pages = computed(() => {
    const unused = this.unusedCodes();
    const pages: EasypassCodeDto[][] = [];
    for (let i = 0; i < unused.length; i += TICKETS_PER_PAGE) pages.push(unused.slice(i, i + TICKETS_PER_PAGE));
    return pages;
  });

  private readonly timeZone = this.data.timeZoneId?.trim() || 'Europe/Vienna';
  readonly issuedBy = this.data.easypass.title ?? '';
  readonly issuedOn = this.formatDay(this.data.easypass.createdAt);
  readonly validUntil = this.formatDay(this.data.easypass.validTo);

  /** The page title while printing — the browser proposes it as the PDF file name. */
  private readonly printTitle = ['Ausfahrtstickets', this.data.easypass.title?.trim(), this.validUntil]
    .filter(Boolean).join(' - ');
  private appTitle: string | null = null;

  private readonly afterPrint = () => {
    document.body.classList.remove('ept-printing');
    if (this.appTitle !== null) {
      document.title = this.appTitle;
      this.appTitle = null;
    }
  };

  ngOnInit(): void {
    window.addEventListener('afterprint', this.afterPrint);

    this.parkingTypeService
      .parkingTypesEasypassesEasypassIdCodesGet(this.data.easypass.id!, this.data.parkingOperatorId)
      .subscribe({
        next: codes => {
          this.codes.set(codes ?? []);
          this.isLoading.set(false);
        },
        error: err => {
          this.error.set(apiErrorMessage(err, ''));
          this.isLoading.set(false);
        }
      });
  }

  ngOnDestroy(): void {
    window.removeEventListener('afterprint', this.afterPrint);
    this.afterPrint();
  }

  redeemUrl(code: EasypassCodeDto): string {
    return `${environment.parkerUrl}/easypass/redeem/${code.token}`;
  }

  print(): void {
    document.body.classList.add('ept-printing');
    this.appTitle ??= document.title;
    document.title = this.printTitle;
    // Let the class apply before the print snapshot is taken.
    setTimeout(() => window.print());
  }

  close(): void {
    this.dialogRef.close();
  }

  /** dd.MM.yyyy of an instant, as the day it is at the site. */
  private formatDay(iso: string | null | undefined): string {
    if (!iso) return '';
    return new Intl.DateTimeFormat('de-AT', {
      timeZone: this.timeZone, day: '2-digit', month: '2-digit', year: 'numeric'
    }).format(new Date(iso));
  }
}
